import wretch from 'wretch';

import { type LoadServerCxModuleOptions } from '@cx-modules/client';
import { configureErrorCatchers } from '@cx-modules/errors';

export type Offers = Offer[];

export interface Offer {
  autoExpandedTermsAndConditions: boolean;
  bookmaker: Bookmaker;
  bookmakerCode: string;
  clickOutUrl?: string;
  clickoutUrl?: {
    url: string;
  };
  customerType: string;
  deviceTypes: any[];
  displayTitle: string;
  displayTitleTemplate: string;
  featuredLabels?: string[];
  fullTerms: string;
  geolocations: Geolocation[];
  id: string;
  offerCta: string;
  offerType: string;
  shortTerms: string;
  summary: string;
  title: string;
  unit: string;
}

export interface Bookmaker {
  displayName: string;
  feedsCode: string;
  geolocations: Geolocation[];
  id: string;
  logoFull: Logo;
  logoShort: Logo;
  name: string;
  paymentMethods: string[];
  payoutSpeed: string;
  primaryColor: string;
  productIdentifier: ProductIdentifier;
  relatedPageLinks: string[];
  review: string;
  reviewPath: string;
  reviewRating: number;
  shortName: string;
}

export interface ProductIdentifier {
  code: string;
  name: string;
}

export interface Logo {
  alt: string;
  type: string;
  url: string;
}

export interface Geolocation {
  countryCode: string;
  subdivisionCode: string;
}

export type OffersResponseData = Array<
  Offer & {
    affiliateId: string;
    clickOutUrl?: string;
    clickoutUrl?: {
      url: string;
    };
    fullTerms?: string;
    id: string;
    summary?: string;
  }
>;

const trimIds = (it?: string) => {
  if (!it) return '';
  return it
    .split(',')
    .map(id => id.trim())
    .filter(Boolean)
    .join(',');
};

export const filterAndStringifyParams = (data: Record<string, unknown>) => {
  const sortedEntries = Object.entries(data)
    .filter(([, value]) => value !== undefined)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, value]) => [key, String(value)]);

  return new URLSearchParams(sortedEntries).toString();
};

export type ClickoutService = 'LEGACY_CLICKOUT_SERVICE' | 'OFFERS_CLICKOUT_SERVICE';

export interface BaseOffersParams {
  apiKey: string;
  clickoutService: ClickoutService;
  clickoutServiceUrl?: string;
  countryCode?: string;
  deviceType?: string;
  fullPageUrl: string;
  geotargeting?: string;
  gtmId?: string;
  offerIds?: string;
  offerLimit?: string;
  pageUrl?: string;
  recommendedMarketId?: string;
  sortBy?: string;
  subdivisionCode?: string;
  vcId?: string;
}

export const fetchOffersDeliveryApiData = async <RequestParams extends Omit<BaseOffersParams, 'clickoutService'>>(
  dataApiEndpoint: string,
  params: Omit<RequestParams, 'dataApiEndpoint'>,
  options?: LoadServerCxModuleOptions,
) => {
  const { apiKey, countryCode, deviceType, offerIds, offerLimit, sortBy, subdivisionCode, vcId } = params;
  if (!apiKey) {
    options?.logger?.error('No apiKey provided');
    throw new Error('No apiKey provided.');
  }

  const request: { variationContainerId: string } | { offerIds: string } | undefined = vcId
    ? { variationContainerId: vcId }
    : offerIds
      ? { offerIds: trimIds(offerIds) }
      : undefined;

  const urlParams = filterAndStringifyParams({
    ...request,
    ...(deviceType && { deviceType }),
    ...(offerLimit ? { limit: offerLimit } : {}),
    ...(sortBy ? { sort: sortBy } : {}),
  });

  const url = new URL(`${dataApiEndpoint}/v1/offers?${urlParams}`);

  const client = configureErrorCatchers(
    wretch(url.toString(), {
      headers: {
        accept: 'application/json',
        'api-key': apiKey,
        'content-type': 'application/json',
        ...(countryCode && { 'country-code': countryCode }),
        ...(subdivisionCode && { 'subdivision-code': subdivisionCode }),
        ...(options?.nocache && { 'Cache-Control': 'no-cache' }),
      },
    }),
    options,
  );

  return client.get().json<OffersResponseData>();
};
